import { useState, useEffect } from "react";
import { useAuth } from "./context/AuthContext";
import axios from "axios";
import LoadingSpinner from "./components/GeneratePage/LoadingSpinner";
import { Spinner } from "@radix-ui/themes";

const apiUrl = import.meta.env.VITE_API_URL;

interface VideoFeedback {
  id: string;
  project_id: string;
  video_id: string;
  video_url: string;
  issue_type: string;
  additional_notes: string | null;
  created_at: string;
  user_email: string;
  project_name: string;
}

export default function VideoFeedbackPage() {
  const [feedbackList, setFeedbackList] = useState<VideoFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, token } = useAuth();

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/api/videos/feedback`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setFeedbackList(response.data);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch video feedback:", err);
        setError(
          "Failed to load video feedback. You may not have permission to view this page."
        );
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      fetchFeedback();
    }
  }, [isAuthenticated, token]);

  // Format date to a readable format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isAuthenticated || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-500">
            Authentication Required
          </h1>
          <p className="mt-2">Please sign in to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-500">
            Error
          </h1>
          <p className="mt-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <h1 className="text-2xl font-bold mb-6">Video Feedback Reports</h1>

      {feedbackList.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
          <p>No video feedback reported yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {feedbackList.map((feedback) => (
            <div
              key={feedback.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden flex flex-col h-full"
            >
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <h2 className="text-lg font-semibold truncate">
                  Issue:{" "}
                  <span className="text-red-600 dark:text-red-500">
                    {feedback.issue_type}
                  </span>
                </h2>
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <p className="truncate">Project: {feedback.project_name}</p>
                  <p className="truncate">Reported by: {feedback.user_email}</p>
                  <p>Reported on: {formatDate(feedback.created_at)}</p>
                </div>
              </div>

              <div className="p-4 flex-grow flex flex-col">
                {feedback.additional_notes && (
                  <div className="mb-4">
                    <h3 className="font-medium mb-2">Additional Notes:</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap line-clamp-3">
                      {feedback.additional_notes}
                    </p>
                  </div>
                )}

                <h3 className="font-medium mb-2">Video:</h3>
                <div className="aspect-video overflow-hidden rounded-md bg-gray-100 dark:bg-gray-700 mt-auto">
                  <video
                    src={feedback.video_url}
                    controls
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
